/**
 * DocumentStatusBadge - Displays document processing status with icon and color
 *
 * Shows the current processing status of a document with appropriate
 * visual styling and iconography.
 */

import { AlertCircle, CheckCircle2, Clock, Loader2, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ProcessingStatus } from '../../types';

export interface DocumentStatusBadgeProps {
  /** The current processing status */
  status: ProcessingStatus | string | undefined | null;
  /** Optional custom className */
  className?: string;
  /** Whether to show the icon */
  showIcon?: boolean;
}

const processingStatusConfig: Record<
  ProcessingStatus,
  {
    label: string;
    icon: React.ReactNode;
    variant: 'default' | 'secondary' | 'outline' | 'destructive';
  }
> = {
  pending: {
    label: 'Pending',
    icon: <Clock className="h-3 w-3" />,
    variant: 'outline',
  },
  processing: {
    label: 'Processing',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
    variant: 'secondary',
  },
  validating: {
    label: 'Validating',
    icon: <Loader2 className="h-3 w-3 animate-spin text-blue-500" />,
    variant: 'secondary',
  },
  completed: {
    label: 'Completed',
    icon: <CheckCircle2 className="h-3 w-3" />,
    variant: 'default',
  },
  failed: {
    label: 'Failed',
    icon: <AlertCircle className="h-3 w-3" />,
    variant: 'destructive',
  },
};

// Default config for unknown statuses
const defaultConfig = {
  label: 'Unknown',
  icon: <Minus className="h-3 w-3" />,
  variant: 'outline' as const,
};

export function DocumentStatusBadge({
  status,
  className,
  showIcon = true,
}: DocumentStatusBadgeProps) {
  // Safely get config with fallback for unknown statuses
  const config =
    status && processingStatusConfig[status as ProcessingStatus]
      ? processingStatusConfig[status as ProcessingStatus]
      : defaultConfig;

  return (
    <Badge variant={config.variant} className={className}>
      {showIcon && config.icon}
      {config.label}
    </Badge>
  );
}
